/** @jsxImportSource @emotion/react */
import React, { memo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '../../../../components/button/button.component';
import { Notification } from '../../../../components/notification/notification.component';
import { IconEdit } from '../../../../assets/icons/icon-edit';

import setEnergyStyles from './set-energy.styles';
import { ISetUpState } from '../../set-up.types';
import { getBatteryEnergyHeight } from '../../utils/getBatteryEnergyHeight';
import { SecurityDeposit } from '../security-deposit/security-deposit.component';

type PropTypes = {
    priceKW: number;
    currency: string;
    onSetSetUpStep: (state: ISetUpState) => void;
    isCarConnected: boolean;
    isFree?: boolean;
    blockingFee?: number;
    hasSecurityDeposit?: boolean;
    showChargingLimit: boolean;
    setShowChargingLimit: (value: boolean) => void;
    securityDepositAmount?: number;
    isCreatingTransaction?: boolean;
    isUnavailable: boolean;
};

function SetEnergy({
    priceKW,
    blockingFee = 0,
    currency,
    onSetSetUpStep,
    isCarConnected,
    isFree = false,
    hasSecurityDeposit = false,
    showChargingLimit,
    setShowChargingLimit,
    securityDepositAmount,
    isCreatingTransaction,
    isUnavailable,
}: PropTypes) {
    const maxEnergy = 100;
    const intl = useIntl();
    const inputRef = useRef<HTMLInputElement>(null);

    const [energy, setEnergy] = useState<number>(30);
    const [showInput, setShowInput] = useState(false);
    const [showEditButton, setShowEditButton] = useState(true);

    const updateEnergy = (value: number) => {
        setEnergy(prevState => {
            return Math.min(maxEnergy, Math.max(5, prevState + value));
        });
    };

    return (
        <div css={setEnergyStyles.container}>
            <div css={setEnergyStyles.columnWrapper}>
                <div css={setEnergyStyles.column} style={{ paddingRight: 15 }}>
                    <div css={setEnergyStyles.infoBlock}>
                        <div css={[setEnergyStyles.infoBlockTitle, { textTransform: 'uppercase' }]}>
                            {intl.formatMessage({ id: 'rate' })}
                        </div>
                        {isFree ? (
                            <div css={setEnergyStyles.infoBlockValue}>
                                {intl.formatMessage({ id: 'payment.free' })}
                            </div>
                        ) : (
                            <div css={setEnergyStyles.infoBlockValue}>
                                {Number(priceKW / 100).toFixed(2)}
                                <div css={setEnergyStyles.infoBlockValueSymbol}>
                                    {currency}&nbsp;/ {intl.formatMessage({ id: 'unit.kWh' })}
                                </div>
                            </div>
                        )}
                    </div>
                    {/* <div css={setEnergyStyles.infoBlock}>
                        <div css={[setEnergyStyles.infoBlockTitle, { textTransform: 'uppercase' }]}>
                            {intl.formatMessage({ id: 'toPay' })}
                        </div>
                        <div css={setEnergyStyles.infoBlockValue}>
                            {(((isFree ? 0 : priceKW) * Number(energy)) / 100).toFixed(2)}
                            <div css={setEnergyStyles.infoBlockValueSymbol}>{currency}</div>
                        </div>
                    </div> */}
                </div>
                <div css={[setEnergyStyles.column, { alignItems: 'center' }]}>
                    <div css={setEnergyStyles.battery}>
                        <div
                            css={[
                                setEnergyStyles.batteryEnergy,
                                { height: getBatteryEnergyHeight(energy) },
                            ]}></div>
                        <div css={setEnergyStyles.batteryEnergyValue}>
                            <div>
                                <div
                                    css={setEnergyStyles.batteryEnergyValueLabel}
                                    onClick={() => {
                                        setShowInput(true);
                                        inputRef.current?.focus();
                                    }}>
                                    {showInput ? (
                                        <input
                                            autoFocus={true}
                                            inputMode={'numeric'}
                                            ref={inputRef}
                                            onFocus={() => setShowEditButton(false)}
                                            onBlur={() => {
                                                setShowEditButton(true);
                                                setShowInput(false);
                                            }}
                                            type={'number'}
                                            css={setEnergyStyles.inputEnergy}
                                            value={energy || undefined}
                                            onChange={event =>
                                                setEnergy(
                                                    Math.min(
                                                        maxEnergy,
                                                        Math.max(
                                                            0,
                                                            Number(
                                                                Number(event.target.value).toFixed(
                                                                    1
                                                                )
                                                            )
                                                        )
                                                    )
                                                )
                                            }
                                        />
                                    ) : (
                                        energy
                                    )}
                                    <span css={setEnergyStyles.energyUnits}>
                                        {intl.formatMessage({ id: 'unit.kWh' })}
                                    </span>
                                    <div css={setEnergyStyles.batteryEnergyValueSymbol}>
                                        {showEditButton && <IconEdit size={12} />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div css={[setEnergyStyles.column, { alignItems: 'center' }]}>
                    <div css={setEnergyStyles.infoBlock}>
                        <div>
                            <Button type={'key'} text={'+'} onClick={() => updateEnergy(5)} />
                        </div>
                    </div>
                    <div css={setEnergyStyles.infoBlock}>
                        <div>
                            <Button type={'key'} text={'–'} onClick={() => updateEnergy(-5)} />
                        </div>
                    </div>
                </div>
            </div>

            <div css={setEnergyStyles.blockingFeeBlock}>
                <span css={setEnergyStyles.blockingFee}>
                    {intl.formatMessage({ id: 'connector.descriptions.blockingFee' })}
                </span>
                <span css={setEnergyStyles.blockingFeeValue}>{`${Number(blockingFee / 100).toFixed(
                    2
                )} ${currency}/${intl.formatMessage({ id: 'min' })}`}</span>
            </div>
            {!isFree && (
                <div css={setEnergyStyles.blockingFeeBlock}>
                    <span css={setEnergyStyles.blockingFee}>
                        {intl.formatMessage({ id: 'connector.descriptions.minimalAmount' })}
                    </span>
                    <span css={setEnergyStyles.blockingFeeValue}>{`${Number(1).toFixed(
                        2
                    )} ${currency}`}</span>
                </div>
            )}

            {hasSecurityDeposit && (
                <SecurityDeposit
                    showChargingLimit={showChargingLimit}
                    setShowChargingLimit={setShowChargingLimit}
                    isCarConnected={isCarConnected}
                    onSetSetUpStep={onSetSetUpStep}
                    amount={securityDepositAmount}
                    currency={currency}
                    isCreatingTransaction={isCreatingTransaction}
                    isUnavailable={isUnavailable}
                />
            )}

            <div css={setEnergyStyles.actionWrapper}>
                <Button
                    isLoading={isCreatingTransaction}
                    disabled={!isCarConnected || isCreatingTransaction || isUnavailable}
                    css={setEnergyStyles.nextButton}
                    text={intl.formatMessage({ id: 'next' })}
                    onClick={() => {
                        onSetSetUpStep({ energy: Number(energy) });
                    }}
                />
            </div>

            <Notification
                text={intl.formatMessage({
                    id: isCarConnected
                        ? 'setEnergy.infoBanner.text'
                        : 'setEnergy.infoBanner.text.notConnected',
                })}
            />
        </div>
    );
}

export default memo(SetEnergy);
